import { getState } from '/bb/state.js';
import { KEYSTROKE_LONG_PAUSE_MS, FOCUS_BY_TAB_MAX_DT_MS } from '/bb/constants.js';
import { welfordAdd } from '/bb/aggregators/welford.js';
import { resolveField } from '/bb/identifier.js';
import { ensureCurrentPage, getOrCreateField } from '/bb/helpers.js';

function isPrintable(key) {
  return typeof key === 'string' && key.length === 1;
}

function isTimingRelevantKey(e) {
  if (e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return false;
  return isPrintable(e.key) || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter';
}

function ensureTimingRuntime(rt) {
  if (!(rt.keystrokesHeld instanceof Map)) rt.keystrokesHeld = new Map();
  if (!Number.isFinite(rt.lastTimingKeyUpAt)) rt.lastTimingKeyUpAt = 0;
}

function resetTimingBoundary(rt) {
  ensureTimingRuntime(rt);
  rt.keystrokesHeld.clear();
  rt.lastTimingKeyUpAt = 0;
}

function keyDownHandler(e) {
  const s = getState();
  const t = Date.now();

  if (e.key === 'Tab') {
    s.transient.lastKey = { wasTab: true, t };
  } else {
    s.transient.lastKey = { wasTab: false, t };
  }

  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (!fld.keystroke) return;
  const rt = fld.runtime;
  ensureTimingRuntime(rt);
  const code = e.code || e.key;
  const isTimingKey = isTimingRelevantKey(e);
  const isFreshTimingKeyDown = isTimingKey && !e.repeat && !rt.keystrokesHeld.has(code);

  if (isFreshTimingKeyDown) {
    if (rt.keystrokesHeld.size > 0) {
      fld.keystroke.overlap_count += 1;
    } else if (rt.lastTimingKeyUpAt > 0) {
      const flight = t - rt.lastTimingKeyUpAt;
      if (flight >= KEYSTROKE_LONG_PAUSE_MS) {
        fld.keystroke.long_pause_count += 1;
      } else if (flight > 0) {
        welfordAdd(fld.keystroke.flight, flight);
      }
    }
    rt.keystrokesHeld.set(code, t);
  }

  if (isPrintable(e.key) || e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter') {
    fld.keystroke.total_count += 1;
    if (fld.keystroke.first_typing_at === 0) fld.keystroke.first_typing_at = t;
    fld.keystroke.last_typing_at = t;
  }
}

function keyUpHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (!fld.keystroke) return;
  const rt = fld.runtime;
  ensureTimingRuntime(rt);
  const code = e.code || e.key;
  const downAt = rt.keystrokesHeld.get(code);
  const t = Date.now();
  if (Number.isFinite(downAt)) {
    const dwell = t - downAt;
    if (dwell > 0) welfordAdd(fld.keystroke.dwell, dwell);
    rt.keystrokesHeld.delete(code);
    if (rt.keystrokesHeld.size === 0) rt.lastTimingKeyUpAt = t;
  }
  rt.lastKeyAt = t;
}

function focusOutHandler(e) {
  const field = resolveField(e.target);
  if (!field) return;
  const pageKey = ensureCurrentPage();
  const fld = getOrCreateField(pageKey, field.formId, field.fieldName, field.type, field.fieldCategories);
  if (!fld.keystroke) return;
  resetTimingBoundary(fld.runtime);
}

function resetAllTimingBoundaries() {
  const s = getState();
  for (const fld of s.fields.values()) {
    if (fld.keystroke && fld.runtime) resetTimingBoundary(fld.runtime);
  }
}

let attached = false;

export function attach() {
  if (attached || typeof document === 'undefined') return;
  document.addEventListener('keydown', keyDownHandler, { passive: true, capture: true });
  document.addEventListener('keyup', keyUpHandler, { passive: true, capture: true });
  document.addEventListener('focusout', focusOutHandler, { passive: true, capture: true });
  document.addEventListener('visibilitychange', resetAllTimingBoundaries, { passive: true });
  if (typeof window !== 'undefined') window.addEventListener('blur', resetAllTimingBoundaries, { passive: true });
  if (typeof window !== 'undefined') window.addEventListener('pagehide', resetAllTimingBoundaries, { passive: true });
  attached = true;
}

export function detach() {
  if (!attached || typeof document === 'undefined') return;
  document.removeEventListener('keydown', keyDownHandler, true);
  document.removeEventListener('keyup', keyUpHandler, true);
  document.removeEventListener('focusout', focusOutHandler, true);
  document.removeEventListener('visibilitychange', resetAllTimingBoundaries);
  if (typeof window !== 'undefined') window.removeEventListener('blur', resetAllTimingBoundaries);
  if (typeof window !== 'undefined') window.removeEventListener('pagehide', resetAllTimingBoundaries);
  resetAllTimingBoundaries();
  attached = false;
}

export { FOCUS_BY_TAB_MAX_DT_MS };
